import { Modal, notification, Spin } from "antd";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { getFormattedDate2 } from '../../../../utils/convertUtil';
import DELETE from "../../../../assets/image/DELETE.png";
import FILE from "../../../../assets/image/multi_file_down01.png";
import PDF from "../../../../assets/image/multi_pdf_down01.png";
import XLS from "../../../../assets/image/icon-xls01.png";
import XLS_SUM from "../../../../assets/image/multi_excel_down01.png";
import AleartMessage from "../../../../component/main/modal/AlertMassage";
import { endpoints } from "../../../../services/endpoint.ts/endpoint";
import { RequestParam, URL } from "../../../../utils/contact";
import JSZip from 'jszip';
import "./Modal.scss";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ModalDelete = ({
  openModal,
  setOpenModal,
  selectedRows,
  handleDeleteItem,
  tableParams,
  openNotificationWithIcon,
  username: propsUsername,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const iframesRef = useRef([]);

  const getExcelSummarys = async () => {
    setIsLoading(true);
    setLoadingMessage(t('history.del.loadingExcelSummary'));
    try {
      const queryString = selectedRows.map((ele) => `ids=${ele.id}`).join("&");
      // 최소 로딩 시간 보장
      await new Promise(resolve => setTimeout(resolve, 800));
      window.location.href = `${URL}${endpoints.GET_EXCEL_SUMMARY.url}?${queryString}`;
      setOpenModal(false);
    } catch (error) {
      notification.error({
        message: t('history.del.downloadFailTitle'),
        description: t('history.del.excelFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setLoadingMessage('');
      }, 1000);
    }
  };

  const getExcel = async () => {
    setIsLoading(true);
    setLoadingMessage(t('history.del.loadingExcel'));
    try {
      // console.log('getExcel RequestParam:', RequestParam(tableParams));
      // 최소 로딩 시간 보장
      await new Promise(resolve => setTimeout(resolve, 800));
      window.location.href = `${URL}${endpoints.GET_EXCEL.url}?${RequestParam(
        tableParams
      )}`;
      setOpenModal(false);
    } catch (error) {
      notification.error({
        message: t('history.del.downloadFailTitle'),
        description: t('history.del.excelFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setLoadingMessage('');
      }, 1000);
    }
  };

  // iframe 정리 함수
  const cleanupIframes = useCallback(() => {
    iframesRef.current.forEach(iframe => {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    });
    iframesRef.current = [];
  }, []);

  // iframe 생성 및 로드 완료 대기 함수
  const createAndLoadIframe = useCallback((id) => {
    return new Promise((resolve, reject) => {
      // iframe 생성
      const iframe = document.createElement('iframe');
      iframe.style.width = '1200px';
      iframe.style.height = '800px';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.src = `/summary/${id}`;
      
      let timeoutId;
      
      // 로드 완료 이벤트
      iframe.onload = () => {
        // 로드 완료 후 추가 대기 시간 단축 (60초 → 2초)
        // console.log(`iframe for ${id} loaded, waiting for content to initialize...`);
        setTimeout(() => {
          const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
          
          // 로딩 스피너가 표시되어 있는지 확인
          const loadingSpinner = iframeDocument.querySelector('.ant-spin');
          if (loadingSpinner) {
            // console.log('로딩 스피너가 아직 표시 중입니다. 추가 대기...');
            
            // 로딩 스피너가 사라질 때까지 최대 10초 대기
            let spinnerCheckCount = 0;
            const spinnerCheckInterval = setInterval(() => {
              spinnerCheckCount++;
              const spinner = iframeDocument.querySelector('.ant-spin');
              if (!spinner || spinnerCheckCount > 20) { // 최대 10초(500ms * 20)
                clearInterval(spinnerCheckInterval);
                // console.log(`Resolving iframe for ${id} after spinner check`);
                clearTimeout(timeoutId); // 타임아웃 제거
                resolve(iframe);
              }
            }, 500);
          } else {
            // console.log(`Resolving iframe for ${id} immediately`);
            clearTimeout(timeoutId); // 타임아웃 제거
            resolve(iframe);
          }
        }, 2000); // 2초로 단축
      };
      
      // 로드 실패 이벤트
      iframe.onerror = () => {
        clearTimeout(timeoutId); // 타임아웃 제거
        reject(new Error(t('history.del.errIframeLoad')));
      };
      
      // 30초 후 타임아웃 (iframe.onload가 호출되지 않는 경우를 대비)
      timeoutId = setTimeout(() => {
        // console.log(`Timeout for iframe ${id}`);
        reject(new Error(t('history.del.errIframeTimeout')));
      }, 30000); // 타임아웃을 30초로 줄임
      
      // body에 추가
      document.body.appendChild(iframe);
      // console.log(`iframe for ${id} appended to body`);
      
      // 관리를 위해 ref에 추가
      iframesRef.current.push(iframe);
    });
    // t 는 언어가 바뀔 때만 새로 생성됩니다. 빼 두면 오류 문구가 이전 언어로
    // 남을 수 있어 의존성에 포함합니다.
  }, [t]);

  // 라우팅 섹션 처리를 위한 별도 함수 - Summary.js와 완전히 동일하게 구현
  const handleRoutingSection = async (section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop) => {
    console.log('라우팅 섹션 처리를 시작합니다.');
    
    try {
      // 페이지에 충분한 공간이 있는지 확인 (최소 200mm 필요)
      const requiredSpace = 200; // 라우팅 섹션에 필요한 최소 공간 (예상치)
      if (currentY + requiredSpace > pageHeight - marginTop) {
        // 새 페이지 추가
        pdfDoc.addPage();
        currentY = marginTop;
      }
      
      // 메인 제목 배경 (어두운 회색으로 수정)
      const titleHeight = 20;
      
      // A4 기준 페이지 너비 (mm)
      const totalPageWidth = 210; // A4 기준
      
      // 실제 콘텐츠 너비 (하드코딩) - 수동 조절 가능
      const titleWidth = 185; // 타이틀 너비 - 필요에 따라 조정 (예: 150-180mm)
      
      // 중앙 정렬을 위한 왼쪽 여백 계산
      const titleStartX = (totalPageWidth - titleWidth) / 2;
      
      // 배경 사각형 그리기
      pdfDoc.setFillColor(255, 255, 255);
      pdfDoc.rect(titleStartX, currentY, titleWidth, titleHeight, 'F');
      
      // 메인 제목 텍스트 (네이비색, 가운데 정렬)
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setTextColor(30, 35, 50);
      pdfDoc.text('Routing Information', titleStartX + (titleWidth / 2), currentY + titleHeight/2 + 6, { align: 'center' });
      
      currentY += titleHeight + 5; // 여백 증가하여 다른 섹션과 일관되게
      
      // 라우팅 섹션 요소 찾기
      const routeContainer = section.querySelector('.route_route_container__2Wjw5') || 
                            section.querySelector('[class*="route_container"]') || 
                            section.querySelector('[class*="route_route_container"]');
      
      if (!routeContainer) {
        console.log('라우팅 컨테이너를 찾을 수 없습니다.');
        return currentY;
      }
      
      // 라우팅 섹션 요소들 찾기
      const routeSections = routeContainer.querySelectorAll('div[class*="route_section"]') || 
                            routeContainer.children;
      
      // 각 라우팅 섹션 처리
      for (let i = 0; i < routeSections.length; i++) {
        const routeSection = routeSections[i];
        
        // 섹션 제목 찾기
        const titleElement = routeSection.querySelector('h3, h4') || 
                            routeSection.querySelector('[class*="title"]');
        
        let sectionTitle = '';
        if (titleElement) {
          sectionTitle = titleElement.textContent.trim();
        }
        
        // 필요한 타이틀인지 확인 (Default Routing, Network Routing 등)
        if (!['Default Routing', 'Network Routing', 'Local Routing', 
              'Broadcast Routing', 'IPv6 Routing'].includes(sectionTitle)) {
          continue; // 관련 없는 섹션은 건너뛰기
        }
        
        // 테이블 찾기
        const table = routeSection.querySelector('table');
        if (!table) continue;
        
        // 행 찾기
        const rows = table.querySelectorAll('tr');
        if (rows.length < 2) continue; // 헤더 + 최소 1개 데이터 행 필요
        
        // 새 페이지 확인
        if (currentY + 60 > pageHeight - marginTop) {
          pdfDoc.addPage();
          currentY = marginTop;
        }
        
        // 서브타이틀(Default Routing 등) 글자 크기 조절
        pdfDoc.setFontSize(9);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.setTextColor(0, 0, 0);
        
        // 서브타이틀도 메인 타이틀과 같은 위치에 배치
        const subtitleStartX = titleStartX;
        pdfDoc.text(sectionTitle, subtitleStartX, currentY);
        currentY += 2; // 서브타이틀과 테이블 사이 간격 줄임 (15mm → 8mm)
        
        // 테이블 설정 - 메인 타이틀과 동일한 크기로 설정
        const tableWidth = titleWidth;
        
        // 헤더 행 처리
        const headerCells = rows[0].querySelectorAll('th, td');
        const headers = Array.from(headerCells).map(cell => cell.textContent.trim());
        
        // 컬럼 너비 계산
        const columnWidths = [];
        if (headers.length === 4) { // 일반적인 경우
          // Network, Gateway, Interface, Flags에 맞게 최적화된 비율
          columnWidths.push(tableWidth * 0.25); // Network
          columnWidths.push(tableWidth * 0.25); // Gateway
          columnWidths.push(tableWidth * 0.2);  // Interface
          columnWidths.push(tableWidth * 0.3);  // Flags
        } else {
          // 균등 분배
          const width = tableWidth / headers.length;
          for (let i = 0; i < headers.length; i++) {
            columnWidths.push(width);
          }
        }
        
        // 헤더 스타일 - 메인 타이틀과 동일한 위치에 그리기
        const headerHeight = 6; // 헤더 높이 줄임 (18mm → 6mm)
        pdfDoc.setFillColor(30, 35, 50); // 헤더 배경색 변경
        pdfDoc.rect(subtitleStartX, currentY, tableWidth, headerHeight, 'F');
        
        // 헤더 텍스트 - 수직 위치 조정
        let xOffset = subtitleStartX;
        for (let j = 0; j < headers.length; j++) {
          // 헤더 텍스트의 수직 위치 조정 (중앙 정렬)
          pdfDoc.setFontSize(8);
          pdfDoc.setTextColor(255, 255, 255); // 텍스트 색상 흰색으로 변경
          pdfDoc.text(headers[j], xOffset + 8, currentY + headerHeight/2 + 1, { 
            align: 'left',
            baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
          });
          xOffset += columnWidths[j];
        }
        
        currentY += headerHeight;
        
        // 데이터 행 처리
        const rowHeight = 8; // 사용자 지정 행 높이 유지
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.setFontSize(8); // 사용자 지정 폰트 크기 유지
        pdfDoc.setTextColor(255, 255, 255); // 데이터 텍스트도 흰색으로 변경
        
        for (let j = 1; j < rows.length; j++) {
          // 페이지 넘김 처리
          if (currentY + rowHeight > pageHeight - marginTop) {
            pdfDoc.addPage();
            currentY = marginTop;
            
            // 헤더 다시 그리기
            pdfDoc.setFillColor(30, 35, 50); // 헤더 배경색 유지
            pdfDoc.rect(subtitleStartX, currentY, tableWidth, headerHeight, 'F');
            
            pdfDoc.setFontSize(8);
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.setTextColor(255, 255, 255); // 헤더 텍스트 색상 유지
            
            xOffset = subtitleStartX;
            for (let k = 0; k < headers.length; k++) {
              pdfDoc.text(headers[k], xOffset + 8, currentY + headerHeight/2 + 1, { 
                align: 'left',
                baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
              });
              xOffset += columnWidths[k];
            }
            
            currentY += headerHeight;
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.setTextColor(255, 255, 255);
          }
          
          // 행 배경색 (모든 행에 동일한 색상 적용)
          pdfDoc.setFillColor(45, 52, 71); // tbody 배경색 변경
          pdfDoc.rect(subtitleStartX, currentY, tableWidth, rowHeight, 'F');
          
          // 셀 데이터 - 수직 위치 조정
          const cells = rows[j].querySelectorAll('td');
          xOffset = subtitleStartX;
          
          for (let k = 0; k < cells.length && k < headers.length; k++) {
            const cellText = cells[k].textContent.trim();
            // 데이터 텍스트의 수직 위치 조정 (중앙 정렬)
            pdfDoc.text(cellText, xOffset + 8, currentY + rowHeight/2 + 1, { 
              align: 'left',
              baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
            });
            xOffset += columnWidths[k];
          }
          
          currentY += rowHeight;
        }
        
        currentY += 10; // 테이블 사이 여백 줄임 (20mm → 10mm)
      }
      
      return currentY;
    } catch (error) {
      console.error('라우팅 섹션 처리 중 오류:', error);
      return currentY + 10;
    }
  };

  // Partition Information 섹션 처리를 위한 별도 함수
  const handlePartitionSection = async (section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop) => {
    try {
      // 페이지에 충분한 공간이 있는지 확인
      if (currentY + 150 > pageHeight - marginTop) {
        pdfDoc.addPage();
        currentY = marginTop;
      }
      
      // 섹션 캡쳐
      const sectionCanvas = await html2canvas(section, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      const sectionImgData = sectionCanvas.toDataURL('image/png');
      const sectionImgProps = pdfDoc.getImageProperties(sectionImgData);
      const sectionImgWidth = pageWidth;
      const sectionImgHeight = (sectionImgProps.height * sectionImgWidth) / sectionImgProps.width;
      
      // 이미지 추가
      pdfDoc.addImage(sectionImgData, 'PNG', marginLeft, currentY, sectionImgWidth, sectionImgHeight);
      currentY += sectionImgHeight + 5;
      
      return currentY;
    } catch (error) {
      console.error('Partition Information 섹션 처리 중 오류:', error);
      return currentY + 10;
    }
  };

  const generatePDFFromIframe = async (iframe, filename) => {
    // 임시 요소 추적을 위한 배열
    const tempElements = [];
    
    try {
      console.time(`PDF 생성 시간 (${filename})`);
      
      // iframe 내부의 document 접근
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      
      // PDF 문서 생성 (여백 설정 추가)
      const pdfDoc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
        margins: { top: 10, right: 10, bottom: 10, left: 10 }
      });
      
      const pageWidth = pdfDoc.internal.pageSize.getWidth() - 20; // 좌우 여백 10mm씩
      const pageHeight = pdfDoc.internal.pageSize.getHeight() - 20; // 상하 여백 10mm씩
      const marginLeft = 10;
      const marginTop = 10;
      
      // 첫 페이지에 "Summary Report" 타이틀 추가
      pdfDoc.setFont("helvetica", "bold");
      pdfDoc.setFontSize(22);
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.text("Summary Report", pageWidth / 2 + marginLeft, marginTop + 8, { align: "center" });
      
      let currentY = marginTop + 15; // 타이틀 아래부터 시작
      currentY += 5;
      
      // 헤더 영역 캡처 (action-buttons 제외)
      const headerSection = iframeDocument.querySelector('.summary-header');
      if (!headerSection) throw new Error(t('history.del.errHeaderSection'));
      
      // 헤더 복제 및 action-buttons 제거
      const headerClone = headerSection.cloneNode(true);
      const actionButtons = headerClone.querySelector('.action-buttons');
      if (actionButtons) {
        actionButtons.remove();
      }
      
      // 임시 요소에 헤더 추가
      const tempHeader = document.createElement('div');
      tempHeader.appendChild(headerClone);
      document.body.appendChild(tempHeader);
      tempElements.push(tempHeader); // 추적 배열에 추가
      
      // 헤더 컨테이너 스타일 설정 - 기존 레이아웃 유지
      tempHeader.style.width = '100%';
      tempHeader.style.padding = '10px';
      
      // 헤더 섹션만 선택하여 글자 크기 키우기
      const headerTextElements = headerClone.querySelectorAll('*');
      headerTextElements.forEach(el => {
        // 텍스트 노드를 포함하는 요소만 처리
        if (el.childNodes && Array.from(el.childNodes).some(node => 
            node.nodeType === Node.TEXT_NODE && node.textContent.trim())) {
          // 글꼴 크기 직접 설정 (계산 방식 사용하지 않음)
          el.style.fontSize = '28px'; // 고정 크기로 설정
          el.style.fontWeight = 'bold';
        }
      });
      
      // 다른 모든 섹션의 글자 크기를 더 작게 조정
      const contentSections = iframeDocument.querySelectorAll('.summary-wapper__down');
      contentSections.forEach(section => {
        const textElements = section.querySelectorAll('*');
        textElements.forEach(el => {
          if (el.childNodes && Array.from(el.childNodes).some(node => 
              node.nodeType === Node.TEXT_NODE && node.textContent.trim())) {
            // 현재 글꼴 크기 가져오기
            const currentSize = window.getComputedStyle(el).fontSize;
            // 글꼴 크기를 더 작게 조정 (0.8배)
            const newSize = parseFloat(currentSize) * 0.7;
            el.style.fontSize = `${newSize}px`;
          }
        });
      });
      
      // 헤더 컬럼 간격 조정 - 기존 구조 유지
      const headerContainer = tempHeader.querySelector('.summary-header');
      if (headerContainer) {
        headerContainer.style.display = 'flex';
        headerContainer.style.flexWrap = 'nowrap';
        headerContainer.style.width = '100%';
        headerContainer.style.gap = '20px';
        
        const infoSectionElement = headerContainer.querySelector('.info-section');
        if (infoSectionElement) {
          infoSectionElement.style.display = 'flex';
          infoSectionElement.style.flexWrap = 'nowrap';
          infoSectionElement.style.width = '100%';
          infoSectionElement.style.gap = '20px';
          
          const headerColumns = infoSectionElement.querySelectorAll('.info-item');
          
          // 첫 번째 컬럼(REPORT NAME)
          if (headerColumns && headerColumns.length > 0 && headerColumns[0]) {
            headerColumns[0].style.cssText = "flex: 1; min-width: 500px;";
            
            // 내부 요소들의 너비도 확장
            const infoLabel = headerColumns[0].querySelector('.info-label');
            const infoValue = headerColumns[0].querySelector('.info-value');
            
            if (infoLabel) {
              infoLabel.style.display = 'inline-block';
              infoLabel.style.marginRight = '10px';
            }
            
            if (infoValue) {
              // info-value 요소의 너비를 명시적으로 설정
              infoValue.style.display = 'inline-block';
              infoValue.style.minWidth = '400px';
              infoValue.style.width = 'auto';
              infoValue.style.maxWidth = '500px';
              infoValue.style.overflow = 'visible';
              infoValue.style.textOverflow = 'clip';
              infoValue.style.whiteSpace = 'nowrap'; // 줄바꿈 방지
            }
          }
          
          // 두 번째 컬럼(COLLECTION DATE) - 사용자가 수정한 300px 유지
          if (headerColumns && headerColumns.length > 1 && headerColumns[1]) {
            headerColumns[1].style.cssText = "flex: 1; min-width: 300px;";
          }
          
          // 세 번째 컬럼(UPLOAD DATE) - 사용자가 수정한 300px 유지
          if (headerColumns && headerColumns.length > 2 && headerColumns[2]) {
            headerColumns[2].style.cssText = "flex: 1; min-width: 300px;";
          }
        }
      }
      
      // html2canvas 옵션 수정 - 스케일 증가
      const headerCanvas = await html2canvas(tempHeader, {
        scale: 2, // 스케일 증가 (기본값 2에서 4로)
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 임시 요소 제거
      try {
        if (tempHeader.parentNode) {
          tempHeader.parentNode.removeChild(tempHeader);
        }
      } catch (e) {
        console.warn('임시 헤더 요소 제거 실패:', e);
      }
      
      // 타이틀 영역 캡처
      const titleSection = iframeDocument.querySelector('.summary-wapper__title');
      if (!titleSection) throw new Error(t('history.del.errTitleSection'));
      
      // 타이틀 복제 및 여백 추가
      const titleClone = titleSection.cloneNode(true);
      const tempTitle = document.createElement('div');
      tempTitle.appendChild(titleClone);
      tempTitle.style.padding = '15px 0'; // 상하 여백 추가
      document.body.appendChild(tempTitle);
      tempElements.push(tempTitle); // 추적 배열에 추가
      
      const titleCanvas = await html2canvas(tempTitle, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 임시 요소 제거
      document.body.removeChild(tempTitle);
      
      // 모든 섹션 가져오기
      const allSections = iframeDocument.querySelectorAll('.summary-wapper__down');
      // console.log(`Found ${allSections.length} sections for ${filename}`);

      if (allSections.length === 0) throw new Error(t('history.del.errSummarySection'));

      // 첫 번째 섹션(요약 정보)만 캡처 - 원래 크기 유지
      const infoSection = allSections[0];
      const infoCanvas = await html2canvas(infoSection, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 첫 페이지에 헤더, 타이틀, 요약 정보 추가
      const headerImgData = headerCanvas.toDataURL('image/png');
      const headerImgProps = pdfDoc.getImageProperties(headerImgData);
      const headerImgWidth = pageWidth;
      const headerImgHeight = (headerImgProps.height * headerImgWidth) / headerImgProps.width;
      
      const titleImgData = titleCanvas.toDataURL('image/png');
      const titleImgProps = pdfDoc.getImageProperties(titleImgData);
      const titleImgWidth = pageWidth;
      const titleImgHeight = (titleImgProps.height * titleImgWidth) / titleImgProps.width;
      
      const infoImgData = infoCanvas.toDataURL('image/png');
      const infoImgProps = pdfDoc.getImageProperties(infoImgData);
      const infoImgWidth = pageWidth;
      const infoImgHeight = (infoImgProps.height * infoImgWidth) / infoImgProps.width;
      
      // 첫 페이지에 헤더 추가
      pdfDoc.addImage(headerImgData, 'PNG', marginLeft, currentY, headerImgWidth, headerImgHeight);
      currentY += headerImgHeight + 5;
      
      // 타이틀 추가
      pdfDoc.addImage(titleImgData, 'PNG', marginLeft, currentY, titleImgWidth, titleImgHeight);
      currentY += titleImgHeight + 3; // 타이틀 아래 여백
      
      // 요약 정보 추가 (한 번만)
      pdfDoc.addImage(infoImgData, 'PNG', marginLeft, currentY, infoImgWidth, infoImgHeight);
      currentY += infoImgHeight + 5;
      
      // 나머지 섹션들 처리
      for (let i = 1; i < allSections.length; i++) {
        const section = allSections[i];
        
        // 라우팅 정보 섹션인지 확인
        const isRoutingSection = (
          section.querySelector('[class*="route_container"]') || 
          section.querySelector('[class*="route_route_container"]') ||
          section.querySelector('.route_route_container__2Wjw5')
        );
        
        // Partition Information 섹션인지 확인 - 타이틀 텍스트 기반
        const partitionTitle = section.querySelector('h3, h4, [class*="title"]');
        const isPartitionSection = partitionTitle && 
                                  partitionTitle.textContent.includes('Partition Information');
        
        // 라우팅 정보 섹션이면 특별히 처리
        if (isRoutingSection) {
          currentY = await handleRoutingSection(section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop);
        }
        // Partition Information 섹션이면 특별히 처리 
        else if (isPartitionSection) {
          currentY = await handlePartitionSection(section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop);
        }
        // 일반 섹션 처리
        else {
          const sectionCanvas = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          });
          
          const sectionImgData = sectionCanvas.toDataURL('image/png');
          const sectionImgProps = pdfDoc.getImageProperties(sectionImgData);
          const sectionImgWidth = pageWidth;
          const sectionImgHeight = (sectionImgProps.height * sectionImgWidth) / sectionImgProps.width;
          
          // 현재 페이지에 섹션이 들어갈 수 있는지 확인
          if (currentY + sectionImgHeight > pageHeight) {
            pdfDoc.addPage();
            currentY = marginTop;
          }
          
          pdfDoc.addImage(sectionImgData, 'PNG', marginLeft, currentY, sectionImgWidth, sectionImgHeight);
          currentY += sectionImgHeight + 5;
        }
      }
      
      // 모든 섹션 처리 후 푸터 추가
      addFooterToAllPages(pdfDoc);

      console.timeEnd(`PDF 생성 시간 (${filename})`);
      
      // PDF를 Blob으로 변환
      const pdfBlob = pdfDoc.output('blob');
      return pdfBlob;
    } catch (error) {
      console.error('PDF 생성 에러:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      
      notification.error({
        message: t('history.del.pdfFailTitle'),
        description: t('history.del.pdfFailDesc'),
        placement: 'top',
        duration: 3,
      });
      throw error; // 오류를 다시 던져서 호출자가 처리할 수 있게 함
    } finally {
      // 모든 임시 요소 안전하게 제거
      tempElements.forEach(element => {
        try {
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }
        } catch (e) {
          console.warn('임시 요소 제거 실패:', e);
        }
      });
      
      setIsLoading(false);
    }
  };

  const getPDF = async () => {
    setIsLoading(true);
    setLoadingMessage(t('history.del.loadingPdf'));
    
    try {
      // 단일 파일 처리
      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        try {
          const iframe = await createAndLoadIframe(row.id);
          const pdfBlob = await generatePDFFromIframe(iframe, row.name);
          
          // iframe 제거
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
            iframesRef.current = iframesRef.current.filter(f => f !== iframe);
          }
          
          // 파일 다운로드 - window.URL 사용
          const filename = `${row.name}-${getFormattedDate2()}.pdf`;
          const url = window.URL.createObjectURL(pdfBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          
          setOpenModal(false);
          openNotificationWithIcon(
            'success',
            t('history.del.pdfDoneTitle'),
            t('history.del.pdfDoneDesc', { name: filename })
          );
        } catch (error) {
          console.error(`${row.name} PDF 생성 에러:`, error);
          openNotificationWithIcon(
            'error',
            t('history.del.pdfFailTitle'),
            t('history.del.pdfOneFailDesc', { name: row.name })
          );
        }
      }
      // 다중 파일 처리
      else {
        const zip = new JSZip();
        const results = [];
        
        // 병렬 처리를 위한 청크 분할 (최대 2개 동시 처리)
        const chunks = [];
        for (let i = 0; i < selectedRows.length; i += 2) {
          chunks.push(selectedRows.slice(i, i + 2));
        }
        
        for (const chunk of chunks) {
          // setLoadingMessage(`PDF 생성 중... (${chunks.indexOf(chunk) * 2 + 1}-${Math.min(chunks.indexOf(chunk) * 2 + chunk.length, selectedRows.length)}/${selectedRows.length})`);
          
          // 각 청크 내 파일 병렬 처리
          const chunkResults = await Promise.all(chunk.map(async (row) => {
            try {
              const iframe = await createAndLoadIframe(row.id);
              const pdfBlob = await generatePDFFromIframe(iframe, row.name);
              
              // ZIP에 추가
              const filename = `${row.name}-${getFormattedDate2()}.pdf`;
              zip.file(filename, pdfBlob);
              
              // iframe 제거
              if (iframe && iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
                iframesRef.current = iframesRef.current.filter(f => f !== iframe);
              }
              
              return { success: true, name: row.name };
            } catch (error) {
              console.error(`${row.name} PDF 생성 에러:`, error);
              return { success: false, name: row.name };
            }
          }));
          
          results.push(...chunkResults);
        }
        
        // 성공 여부 확인
        const successCount = results.filter(r => r.success).length;
        
        if (successCount === 0) {
          throw new Error(t('history.del.pdfAllFailed'));
        }
        
        // ZIP 파일 생성 및 다운로드
        setLoadingMessage(t('history.del.loadingZip'));
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = zipUrl;
        a.download = `PDF_Summary_${getFormattedDate2()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(zipUrl);
        
        setOpenModal(false);
        
        if (successCount < selectedRows.length) {
          openNotificationWithIcon(
          'warning',
          t('history.del.pdfPartialTitle'),
          t('history.del.pdfPartialDesc', {
            done: successCount,
            total: selectedRows.length
          })
        );
        } else {
          openNotificationWithIcon(
          'success',
          t('history.del.pdfDoneTitle'),
          t('history.del.pdfAllDoneDesc', { done: successCount })
        );
        }
      }
    } catch (error) {
      console.error('PDF 생성 에러:', error);
      openNotificationWithIcon('error', t('history.del.pdfFailTitle'), error.message);
    } finally {
      cleanupIframes();
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // PDF 문서에 푸터 추가하는 함수
  const addFooterToAllPages = (pdfDoc) => {
    const pageCount = pdfDoc.internal.getNumberOfPages();
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const footerText = 'Copyright Growin 2025. All Rights Reserved.';
    
    // 모든 페이지에 푸터 추가
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100, 100, 100); // 회색으로 설정
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.text(footerText, pageWidth / 2, pdfDoc.internal.pageSize.getHeight() - 5, { align: 'center' });
    }
    
    return pdfDoc;
  };

  // 컴포넌트 언마운트 시 iframe 정리
  useEffect(() => {
    return () => {
      cleanupIframes();
    };
  }, [cleanupIframes]);

  const getFILE = async () => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: t('history.del.selectRequiredTitle'),
        description: t('history.del.selectRequiredDesc'),
        placement: 'top',
        duration: 3,
      });
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage(t('history.del.loadingFile'));
    try {
      // 최소 로딩 시간 보장
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        const queryString = `username=${encodeURIComponent(row.username)}&fileName=${encodeURIComponent(row.name)}`;
        window.location.href = `${URL}${endpoints.GET_FILE_DOWNLOAD.url}?${queryString}`;
      } else {
        const username = selectedRows[0].username;
        const fileNames = selectedRows.map(row => row.name);
        const queryString = `username=${encodeURIComponent(username)}&${fileNames.map(name => `fileNames=${encodeURIComponent(name)}`).join('&')}`;
        window.location.href = `${URL}${endpoints.GET_MULTIPLE_FILE_DOWNLOAD.url}?${queryString}`;
      }
      
      setOpenModal(false);
      
    } catch (error) {
      notification.error({
        message: t('history.del.downloadFailTitle'),
        description: t('history.del.fileFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setLoadingMessage('');
      }, 1000);
    }
  };

  const showMessage = (title, content, onOk, showCancel = true) => {
    Modal.confirm({
      title: <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-text)' }}>{title}</div>,
      content: content,
      onOk: onOk,
      okText: t('history.del.ok'),
      cancelText: t('history.del.cancel'),
      okButtonProps: {
        style: {
          background: '#1890ff',
          borderColor: '#1890ff',
          borderRadius: '4px',
          padding: '4px 16px',
          height: '32px'
        }
      },
      cancelButtonProps: { 
        style: { 
          display: showCancel ? 'inline-block' : 'none',
          borderRadius: '4px',
          padding: '4px 16px',
          height: '32px',
          marginRight: '8px'
        } 
      },
      className: "custom-modal-confirm",
      icon: null,
      centered: true,
      width: 420,
    });
  };

  const handleDelete = async (selectedRows) => {
    if (selectedRows.length > 0) {
      // 현재 로그인한 사용자와 리포트 소유자 비교
      const username = propsUsername || localStorage.getItem('username') || '';
      const differentOwners = selectedRows.filter(row => row.username !== username);
      const ownedRows = selectedRows.filter(row => row.username === username);
      
      // 다른 소유자들의 사용자명 목록 (중복 제거)
      //
      // 예전에는 여기서 소유자마다 <span> 을 만들어 배열로 넘겼는데, 번역
      // 함수의 보간값은 문자열로 변환되므로 그대로 넘기면 [object Object] 가
      // 찍힙니다. 이름은 평범한 문자열로 넘기고 강조 스타일은 Trans 의
      // owner 슬롯이 입히도록 했습니다.
      const uniqueDifferentOwners = [...new Set(differentOwners.map(row => row.username))];
      const ownerNames = uniqueDifferentOwners.join(', ');
      const ownerSlot = {
        owner: (
          <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px' }} />
        )
      };

      if (ownedRows.length === 0) {
        // 모든 리포트가 다른 소유자의 것인 경우
        showMessage(
          t('history.del.noPermTitle'),
          <div className="modal-message">
            <p>{t('history.del.noPermLine1')}</p>
            <p>
              <Trans
                i18nKey="history.del.noPermLine2"
                values={{ owners: ownerNames }}
                components={ownerSlot}
              />
            </p>
          </div>,
          null,
          false // 취소 버튼 숨기기
        );
      } else if (differentOwners.length > 0) {
        // 일부 리포트가 다른 소유자의 것인 경우
        showMessage(
          t('history.del.confirmTitle'),
          <div className="modal-message">
            <p>{t('history.del.confirmLine1')}</p>
            <p>{t('history.del.confirmLine2')}</p>
            <p>{t('history.del.confirmLine3')}</p>
            <p>
              <Trans
                i18nKey="history.del.confirmLine4"
                values={{ owners: ownerNames }}
                components={ownerSlot}
              />
            </p>
          </div>,
          () => {
            // 소유자가 같은 리포트만 필터링하여 삭제
            handleDeleteItem(ownedRows);
          }
        );
      } else {
        // 모든 리포트의 소유자가 현재 사용자와 같은 경우
        showMessage(
          t('history.del.confirmTitle'),
          <div className="modal-message">
            <p>{t('history.del.confirmLine1')}</p>
          </div>,
          () => handleDeleteItem(selectedRows)
        );
      }

    } else {
      openNotificationWithIcon('warning', t('history.del.selectAtLeastOne'));
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        footer
        onCancel={() => setOpenModal(false)}
        width={400}
        style={{ paddingTop: '20px' }}
      >
        <div className="modal-delete cursor-pointer">
          {/* List Excel download & Summary Excel download */}
          <div className="modal-delete__top">
            <div className="w-50 action-button-wrapper" onClick={() => getExcel()}>
              <div className="action-button">
                <img
                  src={XLS}
                  alt="logo"
                  title="List Excel Down"
                  style={{ width: 48, height: 48 }}
                />
                <p className="mt-0" style={{ color: 'var(--color-text)' }}>List Excel Down</p>
              </div>
            </div>
            <div
              className="w-50 action-button-wrapper"
              onClick={() => {
                selectedRows.length > 0
                  ? getExcelSummarys()
                  : openNotificationWithIcon(
                      "warning",
                      t('history.del.selectAtLeastOne')
                    );
              }}
            >
              <div className="action-button">
                <img
                  src={XLS_SUM}
                  alt="logo"
                  title="Summary Excel Down"
                  style={{ width: 48, height: 60 }}
                />
                <p className="mt-0" style={{ color: 'var(--color-text)' }}> Summary Excel Down</p>
              </div>
            </div>
          </div>

          {/* PDF download & File download */}
          <div className="modal-delete__top">
            <div
              className="w-50 action-button-wrapper"
              onClick={() => {
                selectedRows.length > 0
                  ? getPDF()
                  : openNotificationWithIcon(
                      "warning",
                      t('history.del.selectAtLeastOne')
                    );
              }}
            >
              <div className="action-button">
                <img
                  src={PDF}
                  alt="logo"
                  title="PDF Down"
                  style={{ width: 48, height: 60 }}
                />
                <p className="mt-0" style={{ color: 'var(--color-text)' }}>PDF Down</p>
              </div>
            </div>
            <div
              className="w-50 action-button-wrapper"
              onClick={() => {
                selectedRows.length > 0
                  ? getFILE()
                  : openNotificationWithIcon(
                      "warning",
                      t('history.del.selectAtLeastOne')
                    );
              }}
            >
              <div className="action-button">
                <img
                  src={FILE}
                  alt="logo"
                  title="File Down"
                  style={{ width: 48, height: 60 }}
                />
                <p className="mt-0" style={{ color: 'var(--color-text)' }}> File Down</p>
              </div>
            </div>
          </div>
          {/* Delete selection */}
          <div className="modal-delete__top">
            <div
              className="w-50 action-button-wrapper"
              onClick={() => {
                selectedRows.length > 0
                  ? handleDelete(selectedRows)
                  : openNotificationWithIcon(
                      "warning",
                      t('history.del.selectAtLeastOne')
                    );
              }}
            >
              <div className="action-button">
                <img
                  src={DELETE}
                  alt="logo"
                  title="Delete selection"
                  style={{ width: 40, height: 48 }}
                />
                <p className="mt-0" style={{ color: 'var(--color-text)' }}>Delete selection</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={isLoading}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>{loadingMessage}</p>
        </div>
      </Modal>
      <AleartMessage />
    </>
  );
};

export default ModalDelete;
